/** @file The review of one plan: confidence, ghost switch, commands by name, notes, why Apply failed. */
import type { Doc, Warning } from '@pose2d/format/types';
import type { CatalogKey, Translate } from '@pose2d/i18n/types';
import { WarningList } from '../../import/WarningList';
import { currentDoc } from '../../stores/currentDoc';
import { docStore } from '../../stores/docStore';
import { applyFailureText } from './applyFailureText';
import { AUTORIG_LIMITS } from './autorigLimits.data';
import type { AutorigSession, ReviewView } from './autorigTypes';
import { changedValues } from './changedValues';
import { confidenceBand } from './confidenceBand';
import { planSummary } from './planSummary';

const BAND: Record<'low' | 'medium' | 'high', CatalogKey> = { low: 'autorig.band.low', medium: 'autorig.band.medium', high: 'autorig.band.high' };

type Props = { t: Translate; view: ReviewView; session: Pick<AutorigSession, 'setGhost'>};

const TABLES = ['bones', 'slots', 'images', 'attachments', 'skins', 'constraints', 'events', 'sockets', 'animations'] as const;
const ID_TOKEN = /\b[a-z]+_[0-9A-Z]{26}\b/g;

/** Every entity id of the document with its name; unknown ids are left as they are. */
function namesById(doc: Doc): Map<string, string> {
  const names = new Map<string, string>();
  for (const table of TABLES) {
    for (const [id, entity] of Object.entries(doc[table] as Readonly<Record<string, { name?: string } | undefined>>)) {
      if (entity?.name !== undefined) names.set(id, entity.name);
    }
  }
  return names;
}

/** The notes with entity ids (`bones.bone_01M…`) replaced by the entity's name in their path and message. */
function named(warnings: readonly Warning[], doc: Doc | undefined): readonly Warning[] {
  if (doc === undefined) return warnings;
  const names = namesById(doc);
  const swap = (text: string): string => text.replace(ID_TOKEN, (id) => names.get(id) ?? id);
  return warnings.map((w) => ({ ...w, message: swap(w.message), ...(w.path === undefined ? {} : { path: swap(w.path) }) }));
}

function GhostLine({ t, view, session }: Props) {
  switch (view.preview.kind) {
    case 'ghost':
      return <label className="autorig-ghost"><input type="checkbox" checked={view.ghost} onChange={(e) => { session.setGhost(e.currentTarget.checked); }} />{t('autorig.ghost')}</label>;
    case 'tooLarge':
      return <p className="autorig-note">{t('autorig.ghostTooLarge', { n: view.preview.commands, max: AUTORIG_LIMITS.previewMaxCommands })}</p>;
    case 'failed':
      return <p className="autorig-note">{t('autorig.ghostFailed')}</p>;
    case 'outOfSync':
      return <p className="autorig-note">{t('autorig.ghostOutOfSync')}</p>;
    case 'empty':
      return null;
  }
}

/** Auto-rig everything re-derives every part from the images: on a project with slots, how many of its values Apply changes. */
function RerigLine({ t, view, doc }: { t: Translate; view: ReviewView; doc: Doc | undefined }) {
  if (doc === undefined || view.request.fn !== 'autoRig' || view.preview.kind !== 'ghost') return null;
  const slots = Object.keys(doc.slots).length;
  const n = slots === 0 ? 0 : changedValues(doc, view.preview.doc);
  return n === 0 ? null : <p className="autorig-note" role="status">{t('autorig.rerig', { slots, n })}</p>;
}

/** Server warnings stay in English (spec §4.2); the UI translates only its own sentences. */
export function ReviewBody({ t, view, session }: Props) {
  // The dialog re-renders with the document it is given; names come from the same one (read here as ReviewBody has no doc prop).
  const doc = currentDoc(docStore.getState()) ?? undefined;
  const { plan } = view.reply;
  const summary = planSummary(plan);
  const { percent, band } = confidenceBand(plan.confidence);
  return (
    <div className="autorig-review">
      <p className="autorig-confidence">
        {t('autorig.confidence', { percent, band: t(BAND[band]) })}
        <meter min={0} max={1} low={AUTORIG_LIMITS.low} high={AUTORIG_LIMITS.high} optimum={1} value={plan.confidence} />
      </p>
      <GhostLine t={t} view={view} session={session} />
      {summary.length > 0 && percent === 0 ? <p className="autorig-note" role="status">{t('autorig.zeroConfidence')}</p> : null}
      <RerigLine t={t} view={view} doc={doc} />
      {summary.length === 0 ? <p>{t('autorig.empty')}</p> : (
        <section aria-label={t('autorig.commands', { n: plan.commands.length })}>
          <h4>{t('autorig.commands', { n: plan.commands.length })}</h4>
          <ul className="autorig-summary">{summary.map((s) => <li key={s.name}>{s.name} × {s.count}</li>)}</ul>
        </section>
      )}
      <WarningList t={t} title={t('autorig.warnings', { n: plan.warnings.length })} warnings={named(plan.warnings, doc)} max={AUTORIG_LIMITS.listMax} />
      {view.failure === null ? null : <p role="alert" className="shell-error">{applyFailureText(t, view.failure.thrown)}</p>}
    </div>
  );
}
